<script>
import {
  RECOMENDACAO_TEXT_COLORS,
  RECOMENDACAO_LABELS,
} from '@modules/consolidator/constants/recomendacao'
import {
  isColumnVisible,
  shouldShowActions,
  countVisibleColumns,
} from '@modules/consolidator/utils/column-visibility/column-visibility'
import {
  RENTABILIDADE_COLUMNS,
  WALLET_ASSET_COLUMN_CONFIG,
} from '@modules/consolidator/constants/wallet-asset-columns'
import AssetTags from '@modules/consolidator/components/asset-tags/asset-tags.vue'
import IaSuggestionsPendingBadge from '@modules/crm/components/ia-suggestions-pending-badge/ia-suggestions-pending-badge.vue'
import { SUGESTOES_IA_VIEW } from '@modules/consolidator/constants/Text'
import { openAssetSuggestions } from '@modules/consolidator/utils/asset-row-actions/asset-row-actions'
import TableRowActions from '@modules/consolidator/components/table-row-actions/table-row-actions.vue'
import { podeCurarSugestoesIa } from '@modules/consolidator/utils/suggestions-acesso/suggestions-acesso'

export default {
  name: 'AssetsByGroupTableRow',
  components: { AssetTags, IaSuggestionsPendingBadge, TableRowActions },
  props: {
    asset: { type: Object, default: () => ({}) },
    assetAplicacaoText: { type: String, default: '' },
    assetNegative: { type: Boolean, default: false },
    assetPctText: { type: String, default: '' },
    assetRendText: { type: String, default: '' },
    assetStats: { type: Object, default: () => ({}) },
    assetStatsFmt: { type: Object, default: () => ({}) },
    assetView: { type: Object, default: () => ({}) },
    composicaoFundos: { type: Object, default: () => ({}) },
    modoFiltroCorretora: { type: String, default: '' },
    selectedAssets: { type: Array, default: () => [] },
    showRentability: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    visibleColumns: { type: Array, default: () => [] },
    // Allowlist de acoes por CHAVE (`detalhes`, `editar`, ...). `null` = default,
    // o conjunto inteiro gateado por `readonly`. Array = exatamente essas.
    allowedActions: { type: Array, default: null },
    // Catalogo de colunas INJETAVEL — ver o mesmo prop no group-row.
    columnsConfig: { type: Array, default: () => WALLET_ASSET_COLUMN_CONFIG },
    suitability: { type: Object, default: () => ({}) },
    sugestoesPendentes: { type: Number, default: null },
    ativosDoCliente: { type: Array, default: () => [] },
    movimentacoesDasSugestoes: { type: Object, default: null },
  },
  data() {
    return {
      insideAssetName: '',
    }
  },
  computed: {
    // O selo fica ao LADO do nome, nao no bloco de tags embaixo: embaixo ele aparecia depois da
    // carga e, num relance, a linha parecia nao ter sugestao nenhuma.
    mostraSugestoes() {
      if (this.readonly) return false
      if (
        !podeCurarSugestoesIa({
          user: this.$currentUser(),
          access: this.$access,
        })
      )
        return false
      return this.sugestoesPendentes > 0
    },
    columns() {
      return this.columnsConfig
    },
    hasStats() {
      return Object.keys(this.assetStats || {}).length > 0
    },
    isSaldoEmConta() {
      return this.asset && this.asset['nome_ativo'] === 'Saldo em Conta'
    },
    hasNoRent() {
      return !!this.assetView.showNoRent
    },
    hasAlert() {
      return !!this.assetView.showAlert
    },
    hasMissing() {
      return !!this.assetView.showMissing
    },
    hasRentCols() {
      return !!this.assetView.showRentCols
    },
    selectedAssetsInside: {
      get() {
        return this.selectedAssets || []
      },
      set(value) {
        this.$emit('update-selected-assets', { value })
      },
    },
    corretoraText() {
      if (this.modoFiltroCorretora === 'nome_portfolio') {
        return this.asset.nome_portfolio
      }
      return this.asset['corretora'] || this.asset['Corretora']
    },
    saldoClass() {
      return this.assetNegative ? 'text-danger' : ''
    },
    rendimentoClass() {
      if (this.assetStats.rendimento > 0) return 'text-success'
      if (this.isSaldoEmConta) return 'text-dark'
      return 'text-danger'
    },
    inicioClass() {
      if (this.assetStats.inicio > 0) return 'text-success'
      if (this.isSaldoEmConta) return 'text-dark'
      return 'text-danger'
    },
    recomendacaoLabels() {
      return RECOMENDACAO_LABELS
    },
    recomendacaoStatusColor() {
      return RECOMENDACAO_TEXT_COLORS[this.asset.recomendacao] || '#7C89B7'
    },
    recomendacaoLabel() {
      return (
        this.recomendacaoLabels[this.asset.recomendacao] ||
        this.asset.recomendacao
      )
    },
    rentColspan() {
      return countVisibleColumns({
        visibleColumns: this.visibleColumns,
        columns: RENTABILIDADE_COLUMNS,
      })
    },
    // Os dois botoes de pendencia ("Preencher movimentacoes" / "Inserir
    // informacoes faltantes") abrem o MESMO modal de detalhes. Entao quem manda
    // neles e a disponibilidade da acao `detalhes`, nao o `readonly` cru — na
    // carteira do grupo a tabela e readonly mas `detalhes` esta liberado, e o
    // botao aparecia desabilitado, com o rotulo passivo, sem nada acontecer ao
    // clicar. Sem allowlist o comportamento e o de antes (`!readonly`).
    podeAbrirDetalhes() {
      if (Array.isArray(this.allowedActions)) {
        return this.allowedActions.includes('detalhes')
      }
      return !this.readonly
    },
    showAcoes() {
      return shouldShowActions({
        readonly: this.readonly,
        visibleColumns: this.visibleColumns,
        allowedActions: this.allowedActions,
      })
    },
    rowActions() {
      const isFundOrAsset =
        this.asset && this.asset['Tipo'] !== 'Saldo em Conta'
      const user = this.$currentUser() || {}
      const canLink = isFundOrAsset && (this.$isWhitelabel() || user.IS_CLIENTE)
      const actions = [
        {
          key: 'detalhes',
          icon: 'mdi-text-box-outline',
          tooltip: 'Ver detalhes',
          color: 'primary',
          handler: () => this.handleOpenDetalhes(),
        },
        {
          key: 'editar',
          icon: 'mdi-pencil-outline',
          tooltip: 'Editar',
          color: 'primary',
          handler: () => this.handleOpenNewAsset(),
        },
        {
          key: 'aporte',
          icon: 'mdi-upload',
          tooltip: this.isSaldoEmConta ? 'Deposito' : 'Aporte',
          color: 'green',
          handler: () => this.handleAporteResgate({ action: 'Aplicar' }),
        },
        {
          key: 'resgate',
          icon: 'mdi-download',
          tooltip: this.isSaldoEmConta ? 'Saque' : 'Resgate',
          color: 'red',
          handler: () => this.handleAporteResgate({ action: 'Resgatar' }),
        },
        isFundOrAsset
          ? {
              key: 'portabilidade',
              icon: 'mdi-swap-horizontal',
              tooltip: 'Portabilidade',
              color: 'primary',
              handler: () => this.handleOpenPortabilidade(),
            }
          : null,
        canLink
          ? {
              key: 'vincular',
              icon: 'mdi-link',
              tooltip: 'Vincular a outro ativo',
              color: 'primary',
              handler: () => this.handleOpenVincular(),
            }
          : null,
        {
          key: 'remover',
          icon: 'mdi-delete',
          tooltip: 'Excluir ativo',
          danger: true,
          handler: () => this.handleOpenDelete(),
        },
      ]
      // As entradas condicionais (portabilidade, vincular) vem como `null`, e a
      // allowlist recorta por chave quando o consumidor a declara.
      const existentes = actions.filter(Boolean)
      if (!Array.isArray(this.allowedActions)) return existentes
      return existentes.filter((acao) => this.allowedActions.includes(acao.key))
    },
  },
  methods: {
    handleRevisarSugestoes() {
      const aberto = openAssetSuggestions(
        this.asset,
        this.ativosDoCliente,
        this.movimentacoesDasSugestoes
      )
      if (aberto) return
      this.$showSnack({
        text: SUGESTOES_IA_VIEW.semClienteNoAtivo,
        color: 'error',
        button: true,
      })
    },
    isVisible(column) {
      return isColumnVisible({ visibleColumns: this.visibleColumns, column })
    },
    handleSaveName() {
      this.$emit('change-name', {
        asset: this.asset,
        name: this.insideAssetName,
      })
    },
    handleOpenName() {
      this.insideAssetName = this.asset.nome_ativo
    },
    handleCloseName() {
      this.insideAssetName = ''
    },
    handleOpenDetalhes({ action } = {}) {
      this.$emit('open-detalhes', { asset: this.asset, action })
    },
    handleOpenNewAsset() {
      this.$emit('open-new-asset', { asset: this.asset })
    },
    handleAporteResgate({ action } = {}) {
      this.$emit('aporte-resgate', { asset: this.asset, action })
    },
    handleOpenPortabilidade() {
      this.$emit('open-portability', { asset: this.asset })
    },
    handleOpenVincular() {
      this.$emit('open-vincular', { asset: this.asset })
    },
    handleOpenDelete() {
      this.$emit('open-delete', { asset: this.asset })
    },
  },
}
</script>

<template>
  <tr class="assets-by-group-table-row text-xs text-color-800">
    <template v-for="col in columns">
      <td
        v-if="col.cellType === 'text' && isVisible(col.key)"
        :key="'cell-' + col.key"
        class="assets-by-group-table-row__column"
        :class="
          col.align === 'left'
            ? 'text-left'
            : col.align === 'right'
            ? 'text-right'
            : ''
        "
      >
        <div
          v-if="col.align === 'left'"
          class="assets-by-group-table-row__column--text-ellipsis text-truncate"
        >
          {{ col.value(asset) }}
        </div>
        <span v-else-if="col.cellClass" :class="col.cellClass">{{
          col.value(asset)
        }}</span>
        <template v-else>{{ col.value(asset) }}</template>
      </td>

      <td
        v-else-if="col.key === 'ativo' && isVisible('ativo')"
        :key="'ativo-' + col.key"
        class="assets-by-group-table-row__column"
      >
        <div
          class="assets-by-group-table-row__column--checkbox d-flex align-items-center az-gap-small"
        >
          <v-checkbox
            v-if="!readonly"
            v-model="selectedAssetsInside"
            :value="asset.id"
            class="p-0 m-0"
            dense
            hide-details
            :ripple="false"
            color="primary"
          />
          <div>
            <div class="d-flex align-items-center az-gap-xsmall">
              <div
                class="assets-by-group-table-row__name d-flex align-items-center az-gap-xsmall"
              >
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <span v-on="on">
                      <v-edit-dialog
                        v-if="!readonly"
                        :return-value.sync="insideAssetName"
                        large
                        cancel-text="Cancelar"
                        save-text="Salvar"
                        persistent
                        @save="handleSaveName"
                        @open="handleOpenName"
                        @close="handleCloseName"
                      >
                        {{ asset.nome_ativo || '—' }}
                        <template v-slot:input>
                          <v-text-field
                            v-model="insideAssetName"
                            label="Nome do ativo"
                            single-line
                          />
                        </template>
                      </v-edit-dialog>
                      <template v-else>
                        {{ asset.nome_ativo || '—' }}
                      </template>
                    </span>
                  </template>
                  <span>
                    {{ asset.nome_ativo || '—' }}
                  </span>
                </v-tooltip>
                <v-progress-circular
                  v-if="asset.loadingEdit"
                  indeterminate
                  size="16"
                  color="primary"
                />
                <template
                  v-if="
                    !$currentUser().IS_CLIENTE &&
                    suitability &&
                    suitability.isOutOfMargin
                  "
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" class="d-inline-flex ml-1" v-on="on">
                        <icon-svg
                          name="warning-status"
                          color="#E0AF00"
                          size="16"
                        />
                      </span>
                    </template>
                    <span>
                      Desvio: {{ (suitability.deviation * 100).toFixed(1) }}%
                      (margem: {{ (suitability.margin * 100).toFixed(1) }}%)
                    </span>
                  </v-tooltip>
                </template>
              </div>
              <!-- FORA do `__name`: aquele bloco tem `span { display:inline-block; overflow:hidden;
                   white-space:nowrap }` pra truncar o nome, e a regra pegava o chip do selo por
                   dentro, espremendo icone e contagem um sobre o outro. -->
              <IaSuggestionsPendingBadge
                v-if="mostraSugestoes"
                :pendentes="sugestoesPendentes"
                @revisar="handleRevisarSugestoes"
              />
            </div>

            <div>
              <AssetTags
                class="assets-by-group-table-row__info"
                :asset="asset"
                :asset-view="assetView"
                :composicao-fundos="composicaoFundos"
                :readonly="readonly"
                @open-detalhes="handleOpenDetalhes"
                @open-new-asset="handleOpenNewAsset"
              />
            </div>
          </div>
        </div>
      </td>

      <td
        v-else-if="col.key === 'instituicao' && isVisible('instituicao')"
        :key="'inst-' + col.key"
        class="assets-by-group-table-row__column text-left"
      >
        <div
          class="assets-by-group-table-row__column--text-ellipsis text-truncate"
        >
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <span v-on="on">
                {{ corretoraText }}
              </span>
            </template>
            <span>{{ corretoraText }}</span>
          </v-tooltip>
        </div>
      </td>

      <td
        v-else-if="col.key === 'saldo_valor' && isVisible('saldo_valor')"
        :key="'svalor-' + col.key"
        class="assets-by-group-table-row__column text-right"
      >
        <span class="assets-by-group-table-row__saldo" :class="saldoClass">
          {{ assetAplicacaoText }}
        </span>
      </td>

      <td
        v-else-if="col.key === 'saldo_pct' && isVisible('saldo_pct')"
        :key="'spct-' + col.key"
        class="assets-by-group-table-row__column text-right"
      >
        <span class="assets-by-group-table-row__pct-total" :class="saldoClass">
          {{ assetPctText }}
        </span>
      </td>

      <td
        v-else-if="col.key === 'saldo_data' && isVisible('saldo_data')"
        :key="'sdata-' + col.key"
        class="assets-by-group-table-row__column"
      >
        <div
          v-if="asset.data_referencia_pl"
          style="min-width: 20px; white-space: nowrap"
        >
          {{ asset._d_referencia_pl || '-' }}
        </div>
      </td>

      <template v-else-if="col.key === 'rent_valor'">
        <td
          v-if="showRentability && hasNoRent && rentColspan > 0"
          :key="col.key + '-no-rent'"
          class="assets-by-group-table-row__column"
          :colspan="rentColspan"
        ></td>

        <td
          v-if="showRentability && hasAlert && rentColspan > 0"
          :key="col.key + '-alert'"
          class="assets-by-group-table-row__column"
          :colspan="rentColspan"
        >
          <v-btn
            text
            small
            class="assets-by-group-table-row__button assets-by-group-table-row__button--fill bg-danger text-danger"
            style="font-size: 10px"
            :disabled="!podeAbrirDetalhes"
            @click="handleOpenDetalhes"
          >
            {{
              podeAbrirDetalhes
                ? 'Preencher movimentações'
                : 'Movimentações pendentes'
            }}
          </v-btn>
        </td>

        <td
          v-if="showRentability && hasMissing && rentColspan > 0"
          :key="col.key + '-missing'"
          class="assets-by-group-table-row__column"
          :colspan="rentColspan"
        >
          <v-btn
            text
            small
            class="assets-by-group-table-row__button assets-by-group-table-row__button--missing bg-danger text-danger"
            style="font-size: 10px"
            :disabled="!podeAbrirDetalhes"
            @click="handleOpenDetalhes"
          >
            {{
              podeAbrirDetalhes
                ? 'Inserir informações faltantes'
                : 'Informações faltantes'
            }}
          </v-btn>
        </td>

        <template v-if="showRentability && hasRentCols">
          <td
            v-if="isVisible('rent_valor')"
            :key="'rvalor-' + col.key"
            class="assets-by-group-table-row__column text-right"
          >
            <div
              v-if="hasStats"
              :class="rendimentoClass"
              class="assets-by-group-table-row__rent"
            >
              {{ assetRendText }}
            </div>
          </td>

          <td
            v-if="isVisible('rent_inicio')"
            :key="col.key + '-inicio'"
            class="assets-by-group-table-row__column text-right"
          >
            <div
              v-if="hasStats"
              :class="inicioClass"
              class="assets-by-group-table-row__start"
            >
              {{ assetStatsFmt.inicioFmt }}
            </div>
          </td>

          <td
            v-if="isVisible('rent_data')"
            :key="col.key + '-data'"
            class="assets-by-group-table-row__column"
          >
            <div
              v-if="asset.data_referencia_cotizacao"
              style="min-width: 20px; white-space: nowrap"
            >
              {{ asset._d_referencia_cotizacao || '-' }}
            </div>
          </td>
        </template>
      </template>

      <td
        v-else-if="col.key === 'volatilidade' && isVisible('volatilidade')"
        :key="'vol-' + col.key"
        class="assets-by-group-table-row__column text-right"
      >
        <div v-if="hasStats" class="assets-by-group-table-row__vol">
          {{ assetStatsFmt.volFmt }}
        </div>
      </td>

      <td
        v-else-if="col.key === 'recomendacao' && isVisible('recomendacao')"
        :key="'rec-' + col.key"
        class="assets-by-group-table-row__column"
      >
        <BaseStatus
          v-if="asset.recomendacao"
          soft
          :content="recomendacaoLabel"
          :color="recomendacaoStatusColor"
        />
      </td>

      <td
        v-else-if="col.key === 'comentario' && isVisible('comentario')"
        :key="'com-' + col.key"
        class="assets-by-group-table-row__column"
      >
        <v-tooltip v-if="asset.comentario" top>
          <template v-slot:activator="{ on }">
            <v-icon small color="primary" v-on="on"> mdi-comment-text </v-icon>
          </template>
          <span>{{ asset.comentario }}</span>
        </v-tooltip>
        <v-icon v-else small color="grey"> mdi-comment-outline </v-icon>
      </td>

      <td
        v-else-if="col.key === 'acoes' && showAcoes"
        :key="'acoes-' + col.key"
        class="assets-by-group-table-row__column"
      >
        <div>
          <div :id="'item-menu-' + asset.id">
            <TableRowActions :actions="rowActions" :inline-limit="0" />
          </div>
        </div>
      </td>
    </template>
  </tr>
</template>

<style lang="sass">
$consolidator-border: 1px solid #8E8E8E1A

.assets-by-group-table-row
  border:
    top: $consolidator-border
    bottom: $consolidator-border

  &__column
    padding: 8px 10px

    &--checkbox
      min-height: 45px
      padding-left: 24px

    &--text-ellipsis
      max-width: 250px

  &__name
    max-width: 250px

    span
      display: inline-block
      max-width: 100%
      overflow: hidden
      text-overflow: ellipsis
      white-space: nowrap

  &__info
    display: flex
    justify-content: start
    gap: 4px

  .v-input--selection-controls__input
    margin: 0px !important
</style>
